import { z } from 'zod';

const identifier = z.string().regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/);

const BaseVariableDefinition = z.object({
  required: z.boolean().default(false),
  description: z.string().optional(),
});

// A `list` is a container: it holds the variables its item template reads, so
// the variants that hold a value are defined first and a list is built out of
// them. That ordering is what makes "a list cannot hold a list" a rule of the
// schema rather than a rule every caller has to remember.
const valueVariants = [
  BaseVariableDefinition.extend({
    type: z.literal('string'),
    default: z.string().optional(),
  }),
  BaseVariableDefinition.extend({
    type: z.literal('url'),
    default: z.string().url().optional(),
  }),
  BaseVariableDefinition.extend({
    type: z.literal('data_image_url'),
    default: z.string().optional(),
  }),
  BaseVariableDefinition.extend({
    type: z.literal('number'),
    default: z.number().optional(),
  }),
  BaseVariableDefinition.extend({
    type: z.literal('boolean'),
    default: z.boolean().optional(),
  }),
  BaseVariableDefinition.extend({
    type: z.literal('enum'),
    enum: z.array(z.string()).min(1),
    default: z.string().optional(),
  }),
  BaseVariableDefinition.extend({
    type: z.literal('array'),
    default: z.array(z.string()).optional(),
  }),
] as const;

/** What a list may hold as a child. */
export const LeafVariableDefinitionSchema = z.discriminatedUnion('type', valueVariants);

export const VariableDefinitionSchema = z.discriminatedUnion('type', [
  ...valueVariants,
  BaseVariableDefinition.extend({
    type: z.literal('list'),
    /**
     * The variables one item of this list carries, keyed by name — the list's
     * own children, each with the default the editor's canvas renders it with.
     * Optional so a list is valid before its first child exists.
     */
    vars: z.record(identifier, LeafVariableDefinitionSchema).optional(),
    /**
     * One sample item, whose values are the children's own defaults. Any array
     * is accepted here because the shapes an item may take belong to `vars`;
     * the *values* a list is sent are checked against it (see `schemaFor`).
     */
    default: z.array(z.unknown()).optional(),
  }),
]);

export const VariableDefinitionsSchema = z
  .record(identifier, VariableDefinitionSchema)
  .superRefine((obj, ctx) => {
    if (Object.prototype.hasOwnProperty.call(obj, '__proto__')) {
      ctx.addIssue({ code: 'custom', message: 'invalid key' });
    }

    // enum default membership
    for (const [key, def] of Object.entries(obj)) {
      if (badEnumDefault(def)) {
        ctx.addIssue({
          code: 'custom',
          message: 'default must be one of enum values',
          path: [key, 'default'],
        });
      }

      // A list's children carry the same rules one level down — including the
      // reserved name, since `vars` is keyed exactly like this record and a
      // child definition is what a name there points at.
      if (def.type !== 'list') continue;
      const vars: Record<string, LeafVariableDefinition> = def.vars ?? {};
      if (Object.prototype.hasOwnProperty.call(vars, '__proto__')) {
        ctx.addIssue({ code: 'custom', message: 'invalid key', path: [key, 'vars'] });
      }
      for (const [childKey, child] of Object.entries(vars)) {
        if (badEnumDefault(child)) {
          ctx.addIssue({
            code: 'custom',
            message: 'default must be one of enum values',
            path: [key, 'vars', childKey, 'default'],
          });
        }
      }
    }
  });

/** Whether an enum definition's default is missing from its own choices. */
function badEnumDefault(definition: VariableDefinition): boolean {
  return (
    definition.type === 'enum' &&
    definition.default !== undefined &&
    !definition.enum.includes(definition.default)
  );
}

export type LeafVariableDefinition = z.output<typeof LeafVariableDefinitionSchema>;
export type VariableDefinition = z.output<typeof VariableDefinitionSchema>;
export type VariableDefinitionInput = z.input<typeof VariableDefinitionSchema>;
export type VariableDefinitions = z.output<typeof VariableDefinitionsSchema>;
export type VariableDefinitionsInput = z.input<typeof VariableDefinitionsSchema>;

function schemaFor(def: VariableDefinition): z.ZodTypeAny {
  let base: z.ZodTypeAny;

  switch (def.type) {
    case 'string':
      base = z.string();
      break;
    case 'url':
      base = z.string().url();
      break;
    case 'data_image_url':
      base = z.string();
      break;
    case 'number':
      base = z.number();
      break;
    case 'boolean':
      base = z.boolean();
      break;
    case 'enum':
      // def.enum is guaranteed non-empty by the definitions schema
      base = z.enum(def.enum as [string, ...string[]]);
      break;
    case 'array':
      base = z.array(z.string());
      break;
    case 'list': {
      // A list's value is its items: one record per item, each checked against
      // the children the list declares, so a child value of the wrong type is
      // refused where it is supplied rather than at render time.
      const shape: Record<string, z.ZodTypeAny> = {};
      for (const [name, child] of Object.entries(def.vars ?? {})) shape[name] = schemaFor(child);
      base =
        Object.keys(shape).length === 0
          ? // A list that declares no child has nothing to check an item against.
            z.array(z.record(z.unknown()))
          : z.array(z.object(shape).strip());
      break;
    }
  }

  if (def.required) {
    return base; // must be present & valid
  }
  return base.optional();
}

function buildValuesSchema(defs: VariableDefinitions) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const [name, def] of Object.entries(defs)) {
    shape[name] = schemaFor(def);
  }
  return z.object(shape).strip();
}

export type VariableValueErrors = {
  path: (string | number)[];
  message: string;
}[];

export type VariableValuesResult =
  | { success: true; data: Record<string, unknown> }
  | { success: false; errors: VariableValueErrors };

/**
 * `input` narrowed to the keys `defs` actually define.
 *
 * Used by the branch that returns the caller's own record rather than a parsed
 * one: the record is usable (only required-presence issues were found), but a
 * key no definition covers must not travel with it into storage.
 */
function knownKeysOnly(defs: VariableDefinitions, input: unknown): Record<string, unknown> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};

  const known: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if (key in defs) known[key] = value;
  }
  return known;
}

export function validateVariableValues(
  defs: VariableDefinitions,
  input: unknown,
  enforceRequired = true,
): VariableValuesResult {
  const parsed = buildValuesSchema(defs).safeParse(input);
  if (parsed.success) {
    return { success: true, data: parsed.data };
  }

  const errors = parsed.error.issues
    .filter((i) => !isRequiredError(i))
    .map((i) => ({
      path: i.path,
      message: i.message,
    }));

  // If only required errors existed, treat as success: a missing required value
  // is tolerated here and enforced by the send paths instead.
  if (errors.length === 0) {
    // The caller's own record is returned — this path never produced a parsed
    // one — so it is narrowed to the definitions first. Under a `.strict()`
    // schema that was guaranteed (an unknown key would itself have been an
    // error); with unknown keys merely ignored, returning the record as sent
    // would let a key no definition covers reach storage through this branch.
    return { success: true, data: knownKeysOnly(defs, input) };
  }

  return {
    success: false,
    errors: parsed.error.issues.map((i) => ({
      path: i.path,
      message: i.message,
    })),
  };
}

function isRequiredError(issue: z.ZodIssue) {
  if (issue.code === 'invalid_type') {
    const received = (issue as z.ZodInvalidTypeIssue).received;
    return received === 'undefined';
  }

  if ((issue as any).code === 'required') {
    return true;
  }

  return false;
}

// ─────────────────────────────────────────────────────────────────
// Reading stored definitions
//
// A stored record is jsonb written by an older version of this schema, or by
// hand, so a plain `schema.parse` is the wrong reader: one malformed entry
// would take the whole record down with it. These salvage what parses and
// drop what does not, which is what both the editor (rendering the list) and
// the server (deriving available attribute keys) need.
//
// Lives here rather than in the editor because both sides read the same
// columns — `emails.variable_definition` and `email_lists.attributes` — and a
// second implementation would be a second set of salvage rules to keep in step.
// ─────────────────────────────────────────────────────────────────

/** `identifier` from the schema, plus the reserved name the schema rejects explicitly. */
export const VARIABLE_NAME = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export function parseVariableDefinition(raw: unknown): VariableDefinition | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const base: { required: boolean; description?: string } = {
    required: source['required'] === true,
  };
  if (typeof source['description'] === 'string' && source['description'] !== '') {
    base.description = source['description'];
  }
  const fallback = source['default'];

  switch (source['type']) {
    case 'string':
    case 'url':
    case 'data_image_url':
      return typeof fallback === 'string'
        ? { ...base, type: source['type'], default: fallback }
        : { ...base, type: source['type'] };
    case 'number':
      return typeof fallback === 'number' && Number.isFinite(fallback)
        ? { ...base, type: 'number', default: fallback }
        : { ...base, type: 'number' };
    case 'boolean':
      return typeof fallback === 'boolean'
        ? { ...base, type: 'boolean', default: fallback }
        : { ...base, type: 'boolean' };
    case 'enum': {
      const options = Array.isArray(source['enum'])
        ? source['enum'].filter((option): option is string => typeof option === 'string')
        : [];
      if (options.length === 0) return null;
      return typeof fallback === 'string' && options.includes(fallback)
        ? { ...base, type: 'enum', enum: options, default: fallback }
        : { ...base, type: 'enum', enum: options };
    }
    case 'array': {
      const items = Array.isArray(fallback)
        ? fallback.filter((item): item is string => typeof item === 'string')
        : [];
      return items.length > 0
        ? { ...base, type: 'array', default: items }
        : { ...base, type: 'array' };
    }
    case 'list': {
      const vars = parseListVars(source['vars']);
      const items = Array.isArray(fallback) ? fallback : [];
      const list: Extract<VariableDefinition, { type: 'list' }> = { ...base, type: 'list' };
      if (Object.keys(vars).length > 0) list.vars = vars;
      if (items.length > 0) list.default = items;
      return list;
    }
    default:
      return null;
  }
}

/**
 * The children of a stored list, skipping anything this reader cannot use — a
 * nested list included, since a list holds only the variants that carry a value.
 */
function parseListVars(raw: unknown): Record<string, LeafVariableDefinition> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const vars: Record<string, LeafVariableDefinition> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (name === '__proto__' || !VARIABLE_NAME.test(name)) continue;
    const child = parseVariableDefinition(value);
    if (!child || child.type === 'list') continue;
    vars[name] = child;
  }
  return vars;
}

/** Reads a stored definitions record, skipping anything malformed. */
export function parseVariableDefinitions(raw: unknown): VariableDefinitions {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const definitions: VariableDefinitions = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (name === '__proto__' || !VARIABLE_NAME.test(name)) continue;
    const definition = parseVariableDefinition(value);
    if (definition) definitions[name] = definition;
  }
  return definitions;
}
